import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import { Button } from '../../ui/button';

/**
 * AICaptionPanel
 * Calls generateSocialCaption Firebase function and displays the result.
 * Works for type = 'job' | 'blog' | 'discovery'
 *
 * Props:
 *   type: 'job' | 'blog' | 'discovery'
 *   data: object of fields to pass (role, location, salary, company for job;
 *         title, excerpt, url for blog; mainHeading, taglineText, ctaText for discovery)
 *   onApply: (caption, hashtags) => void  — called when user clicks Apply to Canvas
 */
const AICaptionPanel = ({ type, data, onApply }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(0);

  const hasData = data && Object.values(data).some(v => v && String(v).trim().length > 0);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'generateSocialCaption');
      const res = await fn({ type, data });
      setResult(res.data);
      setSelectedVariation(0);
      // Auto-apply the first variation immediately so the canvas tagline
      // and Instagram Post Copy textarea both populate without an extra click.
      const autoCaption = res.data?.variations
        ? res.data.variations[0]
        : res.data?.caption;
      if (onApply && autoCaption) {
        onApply(autoCaption, res.data?.hashtags || '');
      }
    } catch (err) {
      setError(err?.message || 'Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentCaption = result?.variations
    ? result.variations[selectedVariation]
    : result?.caption;

  const handleApply = () => {
    if (onApply && currentCaption) {
      onApply(currentCaption, result?.hashtags || '');
    }
  };

  return (
    <div style={{
      background: '#0f172a',
      border: '1px solid #312e81',
      borderRadius: 10,
      padding: '14px 16px',
      marginTop: 12,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>✨</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#c4b5fd' }}>
          AI Caption Generator
        </span>
        <span style={{
          fontSize: 10, background: '#1e1b4b', color: '#818cf8',
          borderRadius: 20, padding: '2px 8px', fontWeight: 600
        }}>
          Gemini 3.1 Flash Lite · Free tier
        </span>
      </div>

      {/* Info when no data yet */}
      {!hasData && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>
          {type === 'job' && 'Select a job from the left panel to enable AI caption generation.'}
          {type === 'blog' && 'Select a blog post from the left panel to enable AI caption generation.'}
          {type === 'discovery' && 'Select a discovery template from the left panel to enable AI caption generation.'}
        </div>
      )}

      {/* Generate button */}
      <Button
        onClick={handleGenerate}
        disabled={loading || !hasData}
        style={{
          width: '100%',
          background: hasData ? 'linear-gradient(135deg, #4f46e5, #7c3aed)' : '#1e293b',
          color: hasData ? '#fff' : '#475569',
          border: 'none',
          borderRadius: 7,
          padding: '9px 0',
          fontWeight: 700,
          fontSize: 14,
          cursor: hasData ? 'pointer' : 'not-allowed',
          marginBottom: result || error ? 12 : 0,
        }}
      >
        {loading ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
            Generating with Gemini...
          </span>
        ) : result ? '↺ Regenerate Caption' : '✨ Generate AI Caption'}
      </Button>

      {/* Error */}
      {error && (
        <div style={{
          background: '#1f0a0a', border: '1px solid #7f1d1d',
          borderRadius: 7, padding: '10px 12px', fontSize: 12, color: '#fca5a5',
        }}>
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Variation picker (discovery only) */}
          {result.variations && result.variations.length > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              {result.variations.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setSelectedVariation(i);
                    const cap = result.variations[i];
                    if (onApply && cap) onApply(cap, result?.hashtags || '');
                  }}
                  style={{
                    flex: 1,
                    padding: '6px 0',
                    borderRadius: 6,
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                    background: selectedVariation === i ? '#4f46e5' : '#1e293b',
                    color: selectedVariation === i ? '#fff' : '#94a3b8',
                  }}
                >
                  Version {String.fromCharCode(65 + i)}
                </button>
              ))}
            </div>
          )}

          {/* Caption text */}
          <div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Caption
            </div>
            <textarea
              readOnly
              value={currentCaption || ''}
              style={{
                width: '100%',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 7,
                color: '#e2e8f0',
                fontSize: 13,
                padding: '10px 12px',
                resize: 'vertical',
                minHeight: 80,
                lineHeight: 1.5,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ fontSize: 10, color: '#475569', textAlign: 'right', marginTop: 2 }}>
              {currentCaption?.length || 0} chars
            </div>
          </div>

          {/* Hashtags */}
          {result.hashtags && (
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Hashtags
              </div>
              <div style={{
                background: '#0f2a1a',
                border: '1px solid #14532d',
                borderRadius: 7,
                padding: '8px 12px',
                fontSize: 12,
                color: '#86efac',
                lineHeight: 1.6,
                wordBreak: 'break-word',
              }}>
                {result.hashtags}
              </div>
            </div>
          )}

          {/* Apply button */}
          {onApply && (
            <Button
              onClick={handleApply}
              style={{
                width: '100%',
                background: '#065f46',
                color: '#6ee7b7',
                border: '1px solid #047857',
                borderRadius: 7,
                padding: '8px 0',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              ✓ Apply Caption to Canvas Tagline
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AICaptionPanel;
